import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Button, InputAdornment, Menu, MenuItem, TextField, Typography } from '@mui/material'
import { ButtonIconSeeker, IconSeeker } from '@ui-seeker/core'
import { copyToClipboard } from '@ui-seeker/utils'

import { AppAntSwitch, AppCardTitle, AppInfoButton, AppNote, AppPillToggle, AppTableSkeleton, NoDataFound, headerIconButtonSx, secondaryButtonSx } from '@components'
import { BI_ROUTES, USER_ROUTES } from '@constants'
import { BUSINESS_LOGISTICS_ROUTES } from '@constants/routes'
import { useToast } from '@hooks'
import { slaveId, userData } from '@signals/user'
import { dsColorNew } from '@palette'
import PageWrapper from 'Pages/@PageWrapper'

import ViewBand, { CapturedField, LorField, OfficeField, RangeField, ViewToggle } from '../../@components/ViewBand'
import CarsField from '../../@components/CarsField'
import useDashboardOptions from '../../@hooks/useDashboardOptions'
import { captureWindow, resolveRange } from '../../@services/ranges'
import { fmtDay, fmtRange, fromIso } from '../../@services/dates'
import { applyCompare, parseView, resolveView, storageKey, viewToParams, viewToStored } from '../../@services/viewState'
import { explorerForm, explorerToast, explorerUrl } from '../../@services/explorerLink'
import { NO_CARS, carsLabels, carsOn, shortList } from '../../@services/cars'
import useSeasonData from './@hooks/useSeasonData'
import { buildRequestBody } from './@services/request'
import { buildModel } from './@services/model'
import { marketContext, providerOptions, scopeSubtitle, tierCoverage, visibleProviderIds } from '../../@services/market'
import { CMP_POSSESSIVE, STAT_WORD, cmpPhrase } from './@services/format'
import Grid from './@components/Grid'
import Legend from './@components/Legend'
import DetailDialog from './@components/DetailDialog'
import MarketBar, { TOOL_HEIGHT, toolFieldSx } from '../../@components/MarketBar'
import PriceCurve from '../../@components/PriceCurve'
import { MEDIAN } from './@components/Grid'
import useCellCurve from './@hooks/useCellCurve'
import { buildCurve, curveKey } from '../../@services/curve'
import { curveRequestBody } from './@services/cellCurve'
import { priceCurveParams } from '../../@services/priceCurveLink'
import IdentityChip, { NoIdentityNote } from '../../@components/IdentityChip'
import { columnHeader } from './@components/columns'
import { pickCurrency } from '@utils/currencyDefault'


const SORTS = [['price', 'Price, cheapest first', 'Price'], ['drop', 'Biggest drop first', 'Biggest drop'], ['rise', 'Biggest rise first', 'Biggest rise'], ['name', 'Name, A–Z', 'Name']]
const cardSx = { backgroundColor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '16px', boxShadow: '0 1px 2px rgba(12,26,62,.04)', p: 2 }

const readStored = uid => {
	try { return JSON.parse(localStorage.getItem(storageKey(uid)) || 'null') } catch { return null }
}

const toastBody = (title, lines = []) => <Box>
	<Box sx={{ fontWeight: 500, color: dsColorNew.textTitle }}>{title}</Box>
	{lines.map((l, i) => <Box key={i} sx={{ fontSize: 13, color: dsColorNew.textMuted, mt: 0.25 }}>{l}</Box>)}
</Box>


/**
 * BI dashboard "Season vs last year" (CU-869fmhmx8): how prices for these pick-up dates compare with last year (or
 * last week, or yesterday), and where you stand. Opens with an answer, recomputes on every change of the view, and
 * hands any cell to Advanced (BI) with the same filters.
 */
export default function SeasonVsLastYearPage() {
	const { showToast } = useToast()
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const uid = slaveId.value || userData.value?.uid
	const today = useMemo(() => new Date(), [])
	const options = useDashboardOptions()

	const [view, setView] = useState(null)
	const [rangeTouched, setRangeTouched] = useState(false)
	const [hint, setHint] = useState(null)
	const [rangeOpen, setRangeOpen] = useState(false)
	const [search, setSearch] = useState('')
	const [sort, setSort] = useState('price')
	const [sortAnchor, setSortAnchor] = useState(null)
	const [detail, setDetail] = useState(null)
	const [tierOptions, setTierOptions] = useState(null)

	// CA 6: the URL, then the last view, then the last office used in BI, then the first office BI offers
	useEffect(() => {
		if (options.loading || view) return
		const stored = readStored(uid)
		const v = resolveView(searchParams, stored)
		const ids = options.offices.map(o => Number(o.value))
		const candidates = [parseView(searchParams).office, parseView(stored || {}).office, options.lastOffice, ids[0]]
		v.office = candidates.map(Number).find(id => id && ids.includes(id)) || null
		const lors = options.lors.map(o => o.value)
		if (lors.length && !lors.includes(v.lor)) v.lor = lors.includes(7) ? 7 : lors[0]
		setView(v)
	}, [options.loading])

	// the view lives in the URL ("Copy link") and is remembered per user
	useEffect(() => {
		if (!view) return
		setSearchParams(viewToParams(view), { replace: true })
		try { localStorage.setItem(storageKey(uid), JSON.stringify(viewToStored(view))) } catch {}
	}, [view && viewToParams(view)])

	// the same list and default as Advanced (BI); its links carry this currency (QA obs. 1)
	const currency = pickCurrency(options.currencies, null, { fallbackToFirst: true })
	const pickup = view ? resolveRange(view.range, today, { from: fromIso(view.from), to: fromIso(view.to) }) : null
	const capture = pickup ? captureWindow(view.cmp, pickup.from, pickup.to, today) : null
	const body = view?.office && pickup ? buildRequestBody({ view, pickup, capture, currency: currency?.value }) : null
	const { data, loading, error, retry } = useSeasonData(body)

	// the grid follows the data it shows, so a change being fetched never mislabels the old table
	const shown = useMemo(() => (data && view ? { ...view, cmp: data.compare || view.cmp, by: data.groupBy || view.by, same: data.compare === 'ly' ? !!data.sameDate : view.same } : view), [data, view])
	// E2/E3: Tiers and Providers choose who counts as the market, on the rows already received (no request)
	const tier = view?.tier && tierOptions ? tierOptions.find(t => Number(t.value) === Number(view.tier)) || null : null
	const tierName = tier?.label || null
	const tierProviders = tier ? (tier.providers || []).map(Number) : null
	const picked = view?.tier ? [] : view?.providers || []
	const marketActive = !!tier || !!picked.length
	const visibleKey = `${tier?.value ?? ''}|${picked.join(',')}`
	const visible = useMemo(() => (data && marketActive
		? visibleProviderIds({ providers: data.providers || [], identity: data.identity, tierProviders, picked })
		: null), [data, visibleKey])
	const model = useMemo(() => (data && !data.noResults ? buildModel(data, view?.stat, visible) : null), [data, view?.stat, visible])
	const coverage = t => tierCoverage({ providers: data?.providers || [], identity: data?.identity, tierProviders: (t?.providers || []).map(Number) })
	const carsText = view ? shortList(carsLabels(view, { groups: data?.cars?.groups || [] })) : ''

	// a tier that is not one of the user's tiers is dropped (E4)
	useEffect(() => {
		if (tierOptions && view?.tier && !tierOptions.some(t => Number(t.value) === Number(view.tier))) update({ tier: null })
	}, [tierOptions, view?.tier])
	const headers = useMemo(() => (model ? model.columns.map(c => columnHeader(c, shown.by, today)) : []), [model, shown?.by])

	// E9: the price curve of the cell in the detail, asked when it opens; lines and median composed here
	const detailColumn = detail && model ? model.columns[detail.j] : null
	const curveBody = detailColumn && shown?.office ? curveRequestBody({ view: shown, column: detailColumn, currency: currency?.value }) : null
	const curveState = useCellCurve(curveBody)
	const openedId = detail && detail.rowKey !== MEDIAN ? detail.rowKey : null
	const curve = useMemo(() => (curveState.data ? buildCurve(curveState.data, { stat: view?.stat, visible, openedId }) : null), [curveState.data, view?.stat, visible, openedId])

	const code = data?.currency?.code || currency?.label || 'EUR'
	const officeName = options.offices.find(o => Number(o.value) === Number(view?.office))?.label || ''
	const brand = model?.you?.provider.name || data?.providers?.find(p => p.own === 'primary')?.name

	const update = patch => setView(v => ({ ...v, ...patch }))

	const onCompare = cmp => {
		const next = applyCompare(view, cmp, rangeTouched)
		setView(next.view)
		setHint(next.hint)
	}

	const onRange = patch => {
		setRangeTouched(true)
		setHint(null)
		update(patch)
	}

	const setCars = cars => update({ ...cars })
	const setTier = id => update({ tier: id, providers: [] })
	// as in Advanced (BI): touching Providers drops the tier
	const setProviders = ids => update({ tier: null, providers: ids })
	const clearMarket = () => update({ tier: null, providers: [] })

	const copyLink = () => {
		try {
			copyToClipboard(`${window.location.origin}${BUSINESS_LOGISTICS_ROUTES.SEASON_VS_LY_PATH}?${viewToParams(view)}`)
			const keeps = view.range === 'fixed' ? 'It keeps these fixed dates.' : `It keeps “${resolveRangeLabel(view.range)}”: opened another day, the dates move with it.`
			const filters = [carsOn(view) ? carsText : null, marketActive ? (tierName || `${picked.length} providers`) : null].filter(Boolean)
			showToast.success(toastBody('Link copied', [keeps, ...(filters.length ? [`With its filters: ${filters.join(' · ')}.`] : [])]))
		} catch {
			showToast.error('Could not copy to clipboard')
		}
	}

	const openExplorer = (kind, j = null) => {
		if (!view?.office || !pickup) return
		const columnDates = j === null ? null : model?.columns[j]?.dates
		// E5: the cars as the server resolved them; with a market filter, the providers shown and your brands
		const providerIds = model?.filtered ? model.rows.map(r => r.provider.id) : null
		const market = model?.filtered ? {
			names: model.rows.filter(r => !(model.identity && r.provider.own === 'primary')).map(r => r.provider.name),
			you: model.identity ? model.you?.provider.name || null : null,
		} : null
		const form = explorerForm({ kind, view: shown, pickup, capture, currency: currency?.value, columnDates, resolvedCars: data?.cars, providerIds })
		const t = explorerToast({ kind, view: shown, pickup, columnDates, officeName, explorerTitle: BI_ROUTES.TITLE, resolvedCars: data?.cars, market })
		showToast.info(toastBody(t.title, t.lines))
		window.open(explorerUrl(form, BI_ROUTES.PATH), '_blank', 'noopener')
	}

	const goAcrissGroups = () => {
		showToast.info(toastBody('Opening Settings › Acriss groups'))
		navigate(`${USER_ROUTES.SETTINGS_PATH}?tab=acriss_group`)
	}

	const goSettings = () => {
		showToast.info(toastBody('Opening Settings › Account', ['Your brand is the first one in Identity.']))
		navigate(`${USER_ROUTES.SETTINGS_PATH}?tab=account`)
	}

	const actions = <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
		<AppInfoButton title="About this screen" tooltip="About this screen">
			<p>One question, answered without filling anything in: how are prices for these pick-up dates against last year (or last week, or yesterday), and where do you stand?</p>
			<p>Same prices as {BI_ROUTES.TITLE}: the ones your company has captured. Click any cell to see what is behind it.</p>
		</AppInfoButton>
		<ButtonIconSeeker icon="link" iconWeight="regular" tooltip="Copy link to this view" aria-label="Copy link to this view" sx={headerIconButtonSx} disabled={!view} onClick={copyLink}/>
		<Button variant="outlined" onClick={() => openExplorer('view')} disabled={!view?.office} startIcon={<IconSeeker icon="sliders" sx={{ fontSize: 13 }}/>}
			sx={{ ...secondaryButtonSx, height: 40, color: dsColorNew.webBlue500, fontWeight: 600 }}>
			Open in {BI_ROUTES.TITLE}
		</Button>
	</Box>

	const ready = !!view
	const nProviders = model ? model.rows.filter(r => !(model.identity && r.provider.own === 'primary')).length : 0
	// with a market filter the count, the tier and what the figures are computed among are said once, in the subtitle
	const scope = model?.filtered ? scopeSubtitle({
		shown: model.counts.providers, all: model.counts.allProviders, competitors: model.counts.competitors,
		identity: model.identity, tierName, coverage: tier ? coverage(tier) : null,
	}) : null

	return <PageWrapper
		head={`DS Rentacar | ${BUSINESS_LOGISTICS_ROUTES.SEASON_VS_LY_TITLE}`}
		title={BUSINESS_LOGISTICS_ROUTES.SEASON_VS_LY_TITLE}
		subtitle={BUSINESS_LOGISTICS_ROUTES.SEASON_VS_LY_SUBTITLE}
		isEmpty={false}
		actions={actions}
	>
		{ready && <ViewBand
			toggles={<>
				<ViewToggle label="Compare with">
					<AppPillToggle aria-label="Compare with" value={view.cmp} onChange={onCompare}
						options={[{ value: 'ly', label: 'Last year' }, { value: 'lw', label: 'Last week' }, { value: 'yday', label: 'Yesterday' }]}/>
				</ViewToggle>
				{view.cmp === 'ly' && <Box sx={{ alignSelf: 'flex-end', display: 'flex', alignItems: 'center' }}>
					<Button variant="text" role="checkbox" aria-checked={view.same} onClick={() => update({ same: !view.same })}
						sx={{ textTransform: 'none', color: dsColorNew.textDark, fontSize: 13, fontWeight: 400, gap: 1, px: 1 }}
						startIcon={<Box component="span" sx={{
							width: 16, height: 16, borderRadius: '4px', border: `1.5px solid ${view.same ? dsColorNew.appBlue : '#c3c9d4'}`,
							backgroundColor: view.same ? dsColorNew.appBlue : dsColorNew.white, color: dsColorNew.white, fontSize: 11,
							display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
						}}>{view.same ? '✓' : ''}</Box>}>
						Same date, not same weekday
					</Button>
					<AppInfoButton title="Same weekday or same date" tooltip="Same weekday or same date">
						<p><b>By default</b> each date is compared with the same weekday last year: Friday 16 Oct 2026 with Friday 17 Oct 2025.</p>
						<p><b>Same date</b> compares with the same calendar date, whatever the weekday: 16 Oct 2026 with 16 Oct 2025 (a Thursday). This is what {BI_ROUTES.TITLE} does.</p>
					</AppInfoButton>
				</Box>}
			</>}
			togglesRight={<>
				<ViewToggle label="Show by">
					<AppPillToggle size="small" aria-label="Show by" value={view.by} onChange={by => update({ by })}
						options={[{ value: 'week', label: 'Week' }, { value: 'day', label: 'Day' }]}/>
				</ViewToggle>
				<ViewToggle label="Price per provider">
					<AppPillToggle size="small" aria-label="Price per provider" value={view.stat} onChange={stat => update({ stat })}
						options={[{ value: 'min', label: 'Min' }, { value: 'avg', label: 'Avg' }, { value: 'max', label: 'Max' }]}/>
				</ViewToggle>
			</>}
			fields={{
				office: <OfficeField value={view.office} options={options.offices} onChange={office => update({ office })}/>,
				lor: <LorField value={view.lor} options={options.lors} onChange={lor => update({ lor })}/>,
				cars: <CarsField view={view} resolved={data?.cars} onChange={setCars} onOpenSettings={goAcrissGroups}/>,
				pickup: <RangeField value={view.range} from={view.from} to={view.to} today={today} hint={hint} onChange={onRange} open={rangeOpen} onOpenChange={setRangeOpen}/>,
				captured: <CapturedField until={capture ? (view.cmp === 'ly' ? fmtDay(capture.to) : fmtRange(capture.from, capture.to)) : null}/>,
			}}
		/>}

		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
			{ready && !!pickup && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', fontSize: 13, color: dsColorNew.textMuted }}>
				<Box component="span">
					<b style={{ color: dsColorNew.textTitle }}>{officeName}</b> · {view.lor}-day rentals
					{carsOn(view) && <> · <span style={{ color: dsColorNew.appBlue, fontWeight: 600 }}>{carsText}</span></>}
					{' '}· pick-ups <b style={{ color: dsColorNew.textTitle }}>{fmtRange(pickup.from, pickup.to)}</b> · {STAT_WORD[view.stat]} per provider · {cmpPhrase(view.cmp, view.same)}
					{marketActive && <> · <span style={{ color: dsColorNew.appBlue, fontWeight: 600 }}>{marketContext(tierName)}</span></>}
				</Box>
				<Box sx={{ flex: 1 }}/>
				{!!data?.identity && !!brand && <IdentityChip brand={brand} onChange={goSettings}/>}
			</Box>}

			{!!data && !data.identity && <NoIdentityNote onGo={goSettings}/>}

			<Box component="section" sx={cardSx}>
				<Box sx={{ mb: 1.5 }}>
					<AppCardTitle icon="chart-column" title={`Prices by provider and pick-up ${shown?.by === 'day' ? 'date' : 'week'}`}
						subtitle={cardSubtitle({ ready, loading, data, model, nProviders, by: shown?.by, cars: carsOn(view), scope, onClearMarket: clearMarket })} sx={{ minWidth: 0 }}>
						<AppInfoButton title="How to read this table" tooltip="How to read this table">
							<p><b>Each cell</b> is the provider's price for that pick-up date (Min, Avg or Max of the prices captured) and how much it changed against the comparison, in % and in {code}.</p>
							<p><b>Market median</b> is the median of your competitors' prices for that date. Your brands are not in it.</p>
							<p><b>Tiers and Providers</b> choose who counts as the market: Market median, Your position and Cheapest are computed among the providers shown. Your brand is always shown and always counts. <b>Find a provider</b> only finds a row: it does not change those figures.</p>
							<p><b>Your position</b>: 1 is the cheapest. <b>Gap to cheapest</b>: how much dearer you are than the cheapest provider; if you are the cheapest, how much cheaper than the second.</p>
							<p><b>Few prices</b> (<span style={{ color: dsColorNew.noteAmber, fontWeight: 700 }}>!</span>): fewer than 5 prices behind the cell in one of the two periods. The change may come from what was being watched, not from a price move.</p>
						</AppInfoButton>
					</AppCardTitle>
					{/* one toolbar row (polish 2026-10-26): one height, one grey border, labels inside; it wraps when narrow */}
					{!!model && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mt: 1.5 }}>
						<MarketBar
							tier={tier ? Number(tier.value) : null} onTierOptions={setTierOptions} onTier={setTier}
							options={providerOptions({ providers: data.providers || [], identity: data.identity })}
							shown={tier ? coverage(tier).ids : picked.filter(id => (data.providers || []).some(p => Number(p.id) === Number(id)))}
							coverage={coverage} active={marketActive} onProviders={setProviders}
						/>
						<TextField size="small" placeholder="Find a provider" value={search} onChange={e => setSearch(e.target.value)}
							onKeyDown={e => { if (e.key === 'Escape') setSearch('') }}
							inputProps={{ 'aria-label': 'Find a provider' }}
							InputProps={{ startAdornment: <InputAdornment position="start"><IconSeeker icon="magnifying-glass" sx={{ fontSize: 13, color: dsColorNew.textSubtle }}/></InputAdornment> }}
							sx={{ flex: '0 1 200px', minWidth: 160, ...toolFieldSx() }}/>
						<Button variant="outlined" onClick={e => setSortAnchor(e.currentTarget)} aria-haspopup="listbox"
							sx={{ ...secondaryButtonSx, height: TOOL_HEIGHT, borderRadius: '10px', fontSize: 13, backgroundColor: dsColorNew.filterInputBg, whiteSpace: 'nowrap', flexShrink: 0 }}>
							<Box component="span" sx={{ color: dsColorNew.textMuted, mr: 0.5 }}>Sort competitors:</Box>{SORTS.find(s => s[0] === sort)[2]} ▾
						</Button>
						<Menu anchorEl={sortAnchor} open={!!sortAnchor} onClose={() => setSortAnchor(null)}>
							{SORTS.map(([value, label]) => <MenuItem key={value} selected={value === sort} onClick={() => { setSort(value); setSortAnchor(null) }}>{label}</MenuItem>)}
						</Menu>
						<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, height: TOOL_HEIGHT, flexShrink: 0 }}>
							<AppAntSwitch checked={!!view.prev} onChange={() => update({ prev: !view.prev })} inputProps={{ 'aria-label': `Show ${CMP_POSSESSIVE[view.cmp]} price` }}/>
							<Typography sx={{ fontSize: 13, color: dsColorNew.textDark, cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => update({ prev: !view.prev })}>Show {CMP_POSSESSIVE[view.cmp]} price</Typography>
						</Box>
						{marketActive && <Button variant="text" onClick={clearMarket} sx={{ textTransform: 'none', color: dsColorNew.textMuted, height: TOOL_HEIGHT, whiteSpace: 'nowrap', flexShrink: 0 }}>Clear filters</Button>}
					</Box>}
				</Box>

				<Box sx={{ position: 'relative' }}>
					{renderBody({
						ready, options, data, error, loading, model, headers, shown, code, search, sort, retry,
						officeName, lor: view?.lor, tierName, carsText: carsOn(view) ? carsText : null,
						onOpen: (rowKey, j) => setDetail({ rowKey, j }),
						onClearMarket: clearMarket,
						onAllCars: () => setCars({ ...NO_CARS, carsIds: [] }),
						onClearSearch: () => setSearch(''),
						onChangeDates: () => setRangeOpen(true),
						onExplorer: () => openExplorer('view'),
					})}
					{loading && !!data && <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 7.5, zIndex: 6 }}>
						<Box component="span" sx={{ backgroundColor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '999px', px: 1.75, py: 0.75, fontSize: 13, color: dsColorNew.textMuted, boxShadow: '0 12px 32px rgba(12,26,62,.12)' }}>Updating…</Box>
					</Box>}
				</Box>

				{!!model && <Legend cmp={shown.cmp} code={code}/>}
			</Box>
		</Box>

		<DetailDialog open={!!detail && !!model} target={detail} model={model} headers={headers} view={shown} code={code} officeName={officeName}
			filters={{ tierName, carsText: carsOn(view) ? carsText : null }}
			onClose={() => setDetail(null)} onExplorer={(kind, j) => openExplorer(kind, j)}
			onPriceCurve={j => navigate(`${BUSINESS_LOGISTICS_ROUTES.PRICE_CURVE_PATH}?${priceCurveParams({ view: shown, dates: model.columns[j].dates, by: shown.by })}`)}>
			{() => <PriceCurve key={curveBody ? curveKey(curveBody) : 'none'} curve={curve} loading={curveState.loading} error={curveState.error} onRetry={curveState.retry}
				stat={view.stat} lor={shown.lor} code={code} today={today} onExplorer={() => openExplorer('curve', detail.j)}/>}
		</DetailDialog>
	</PageWrapper>
}

const resolveRangeLabel = key => ({
	next4w: 'Next 4 weeks', next8w: 'Next 8 weeks', next12w: 'Next 12 weeks', curNext: 'Current and next month',
	last4w: 'Last 4 weeks', last8w: 'Last 8 weeks',
})[key] || key

function cardSubtitle({ ready, loading, data, model, nProviders, by, cars, scope, onClearMarket }) {
	if (!ready || (loading && !data)) return 'Loading…'
	if (data?.noResults) return cars ? 'No prices for these cars' : 'No prices for this view'
	if (!model) return ''
	const n = model.columns.length
	const thin = model.thinCells
	const tail = ` · ${n} ${by === 'day' ? 'days' : 'weeks'}` + (thin ? ` · ${thin} cell${thin > 1 ? 's' : ''} with few prices` : '')
	if (!scope) return `${nProviders} ${model.identity ? 'other providers' : 'providers'}${tail}`
	return <>
		{scope.text}{tail} · <Button variant="text" size="small" onClick={onClearMarket}
			sx={{ textTransform: 'none', p: '0 4px', minWidth: 0, fontSize: 13, verticalAlign: 'baseline', lineHeight: 'inherit' }}>{scope.action}</Button>
	</>
}

function renderBody({ ready, options, data, error, loading, model, headers, shown, code, search, sort, retry, officeName, lor, tierName, carsText, onOpen, onClearSearch, onChangeDates, onExplorer, onClearMarket, onAllCars }) {
	if (ready && !options.loading && !options.offices.length) {
		return <NoDataFound title="No offices to show" text="Business Intelligence has no offices for your account yet."/>
	}
	if (error && !loading) {
		// a 400 is the view's length, not the server: retrying would not help
		if (error?.status === 400) return <AppNote severity="error" sx={{ my: 1 }}>This view is too long: choose fewer pick-up dates.</AppNote>
		return <AppNote severity="error" sx={{ my: 1 }}>
			<b>Could not load the prices.</b> The server did not answer in time. Your view is kept: try again in a moment.
			<Box sx={{ mt: 1 }}><Button variant="outlined" onClick={retry} sx={secondaryButtonSx}>Try again</Button></Box>
		</AppNote>
	}
	if (!ready || !data) return <AppTableSkeleton rows={9} columns={4}/>
	if (data.noResults && carsText) {
		// E1: no prices for the cars picked (or groups that resolve to no code): never all cars in silence
		const which = carsText.includes('#') ? 'these cars' : carsText
		return <Box>
			<NoDataFound title="No prices for these cars" text={`Your company has not captured prices for ${which} at ${officeName}, ${lor}-day rentals, in these dates.`}/>
			<Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1.5 }}>
				<Button variant="outlined" onClick={onAllCars} sx={secondaryButtonSx}>Show all cars</Button>
				<Button variant="text" onClick={onExplorer} sx={{ textTransform: 'none', color: dsColorNew.textMuted }}>Open in {BI_ROUTES.TITLE}</Button>
			</Box>
		</Box>
	}
	if (data.noResults) {
		return <Box>
			<NoDataFound title="No prices for these pick-up dates" text={`Your company has not captured prices for ${officeName}, ${lor}-day rentals, in these dates. Try another office or another range.`}/>
			<Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1.5 }}>
				<Button variant="outlined" onClick={onChangeDates} sx={secondaryButtonSx}>Change pick-up dates</Button>
				<Button variant="text" onClick={onExplorer} sx={{ textTransform: 'none', color: dsColorNew.textMuted }}>Open in {BI_ROUTES.TITLE}</Button>
			</Box>
		</Box>
	}
	return <Grid model={model} headers={headers} view={shown} code={code} search={search} sort={sort} onOpen={onOpen} onClearSearch={onClearSearch} tierName={tierName} onClearMarket={onClearMarket}/>
}
